import React, { useRef, useState } from "react";
/** Assets */
import defaultBackGroundImage from "assets/images/defaultAppBackgroundImage.png";
import { Bars3Icon, PlusIcon } from "@heroicons/react/24/outline";
import HomeIcon from "assets/icons/HeroIcons/HomeIcon";
import { BellIcon } from "assets/icons/HeroIcons";
import {
  ArrowUpRightIcon,
  BinocularsIcon,
  CameraIcon,
  CheckCircleIcon,
  FolderListIcon,
  User2Icon,
} from "assets/icons/HeroIcons";
import ISNLogo from "assets/logo/ISNLogo";
/** Components */
import { Toasts } from "view/components/Toasts";
/** Services */
import apiLibrary from "services/api";
import { getRandomProgress } from "utils/getRandomProgress";
import { setLoadingProgress } from "store/loadingBar";
import { useDispatch } from "react-redux";

interface IProps {
  setFieldValue?: any;
  values?: any;
  editMode?: boolean;
}

export const ObserverMobileDashboard: React.FC<IProps> = ({
  setFieldValue,
  values,
  editMode,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  /** Image upload function */
  const handleImageUpload = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      try {
        /** Validate image */
        const imageValidation = [
          "image/jpg",
          "image/jpeg",
          "image/png",
        ];
        if (!imageValidation.includes(file.type)) {
          event.target.value = "";
          return Toasts.error("Please select a valid image file (jpg/png/jpeg)");
        }

        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 25) {
          return Toasts.error("File size must be less than 25 mb!");
        }
        /** Validate image dimensions */
        const dimensionsValid = await new Promise<boolean>((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (img.width === 1080 && img.height === 1920) {
              resolve(true);
            } else {
              Toasts.error("Image dimension must be exactly 1080 x 1920 px");
              resolve(false);
            }
          };
          img.onerror = () => {
            Toasts.error("Failed to read image dimensions");
            resolve(false);
          };
          img.src = URL.createObjectURL(file);
        });

        if (!dimensionsValid) {
          event.target.value = "";
          return;
        }
        dispatch(setLoadingProgress(getRandomProgress()));
        /** Upload file */
        const uploadResponse = await apiLibrary.file.fileUpload(
          file,
          false,
          "public"
        );
        setFieldValue("observerMobileDashboardImageId", uploadResponse.data);
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message ?? error.message;
        Toasts.error(errorMsg);
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        dispatch(setLoadingProgress(100));
      }
    }
  };

  /** function that trigger onclick */
  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  /** Function to set background color to default */
 const resetImage = async () => {
  try {
    dispatch(setLoadingProgress(getRandomProgress()));
    await new Promise((resolve) => setTimeout(resolve, 300)); 
    setFieldValue("observerMobileDashboardImageId", null);

    dispatch(setLoadingProgress(100));
  } catch (error) {
    Toasts.error("Error resetting image.");
    dispatch(setLoadingProgress(100));
  }
};
  return (
    <div className="w-full h-[342px] col-span-1 ">
      <div>
        <div>
          <p className="font-semibold text-secondaryMid dark:text-textMain text-base">
            Observer Mobile Dashboard
          </p>
          <p className="text-sm text-textMidLight dark:text-textMain">
            For the best results on all devices,
            <br /> use an image that's 1080 x 1920 px.
          </p>
          {!editMode && (
            <button
              type="button"
              disabled={values.observerMobileDashboardImageId === null}
              onClick={resetImage}
              className={`text-primary text-xs font-semibold mt-2  ${values.observerMobileDashboardImageId === null ? " cursor-not-allowed " : "cursor-pointer"} `}
            >
              Reset to default
            </button>
          )}
        </div>
        <div
          className={`relative w-[140px] bg-primary_9 dark:bg-bgMain rounded-t-lg ${editMode ? "mt-4" : "mt-0"} `}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center h-[195px] rounded-lg z-10"
            style={{
              backgroundImage: `url(${values.observerMobileDashboardImageId?.file ? `"${values.observerMobileDashboardImageId?.file.original}"` : defaultBackGroundImage})`,
            }}
          >
          </div>

          {/* Status Bar */}
          <div className="px-1 py-1 flex justify-between items-center text-white text-sm relative z-10">
            <span className="text-[7.52px] text-textWhite">9:30</span>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            {!editMode && (
              <button
                type="button"
                onClick={triggerImageUpload}
                className="h-8 w-8 disabled:cursor-not-allowed flex justify-center items-center rounded-full bg-bgWhite dark:bg-secondaryLight absolute right-[-10px] top-[-12px]"
              >
                <CameraIcon className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="flex items-center w-full relative px-1 z-10">
            <div className="w-full h-[9px] bg-primary rounded-md flex items-center">
              <div className="w-[40%] h-[8px]  bg-bgWhite rounded-md ml-0.5"></div>
            </div>
          </div>
          {/* Header */}
          <div className="bg-blue-700 px-1 py-3 relative z-20">
            <div className="flex items-center justify-between mb-4">
              <Bars3Icon className="w-3 h-3 text-textWhite" />
              <div className="bg-primaryExtraLight p-1 rounded-sm">
                <ISNLogo width={8} height={8} />
              </div>
              <div className="bg-primaryExtraLight p-1 rounded-full">
                <User2Icon className="w-2 h-2 " fill="#005C89" />
              </div>
            </div>

            <h1 className="text-textWhite text-[9.52px] font-semibold">
              Welcome, Amanda
            </h1>
          </div>

          {/* Main Content Area */}
          <div className=" px-1 space-y-4 relative z-20">
            {/* Dashboard Cards */}
            <div className="flex justify-between">
              {/* Large Card */}
              <div className="bg-primaryLightSilver rounded-md p-6 h-[54.82px] w-[74px] relative shadow-lg">
                <div className="absolute bottom-1 right-1 w-3 h-3 bg-bgGreyish_77 rounded-full flex items-center justify-center">
                  <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                </div>
              </div>

              {/* Small Cards Column */}
              <div className="flex flex-col justify-between">
                <div className="bg-primaryLightSilver rounded-md p-3 h-[26px] w-[54px] relative shadow-lg">
                  <div className="absolute bottom-1 right-1 w-3 h-3 bg-bgGreyish_77 rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
                <div className="bg-primaryLightSilver rounded-md p-3 h-[26px] w-[54px] relative shadow-lg">
                  <div className="absolute bottom-1 right-1 w-3 h-3 bg-bgGreyish_77 rounded-full flex items-center justify-center">
                    <ArrowUpRightIcon className="w-2 h-2" fill="#FFFFFF" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Measurement Display with dashed border */}
          <div className="relative mt-[25px] px-1 h-[80px]">
            {/* Blue measurement label */}
            <div className="flex justify-between absolute top-[-15px] gap-1 z-20">
              <div className="bg-bgWhite dark:bg-bgtetriary h-[47px] w-[63px]  rounded-md shadow-lg relative">
                <div className="absolute top-1 right-1">
                  <CheckCircleIcon className="w-3 h-3" fill="#009F88" />
                </div>
                <div className="absolute bottom-1 right-1 w-3 h-3 bg-bgBlusish_7 dark:bg-secondaryMidLight rounded-full flex items-center justify-center">
                  <ArrowUpRightIcon className="w-2 h-2" fill="#005C89" />
                </div>
              </div>
              <div className="bg-bgWhite dark:bg-bgtetriary h-[47px] w-[63px] rounded-md shadow-lg relative">
                <div className="absolute top-1 right-1">
                  <CheckCircleIcon className="w-3 h-3" fill="#009F88" />
                </div>
                <div className="absolute bottom-1 right-1 w-3 h-3 bg-bgBlusish_7 dark:bg-secondaryMidLight rounded-full flex items-center justify-center">
                  <ArrowUpRightIcon className="w-2 h-2" fill="#005C89" />
                </div>
              </div>
            </div>
            {/* Add Observation Button */}

            <div className="px-1 relative z-10 top-[43px]">
              <button
                type="button"
                className="w-full bg-primary py-1 gap-1 text-[9.15px] font-bold rounded-xl flex items-center justify-center text-textWhite"
              >
                <PlusIcon className="w-2.5 h-2.5" />
                <span>Add Observation</span>
              </button>
            </div>
          </div>
          {/* Bottom Navigation */}
          <div className="bg-bgWhite border-t dark:bg-secondaryLight border-borderLightGray dark:border-bgMain px-2 relative z-10 shadow-sm">
            <div className="flex justify-between items-center gap-2">
              <div className="flex flex-col items-center border-b border-b-primary h-full w-full py-[3px]">
                <HomeIcon className="w-3 h-3" fill="#005C89" />
                <span className="text-[4px] font-medium text-primary">
                  Home
                </span>
              </div>
              <div className="flex flex-col items-center  w-full py-[3px]">
                <BinocularsIcon className="w-3 h-3" />
                <span className="text-[4px] font-medium text-secondaryMid dark:text-textWhite">
                  Observations
                </span>
              </div>
              <div className="flex flex-col items-center  w-full py-[3px]">
                <FolderListIcon className="w-3 h-3" />
                <span className="text-[4px] font-medium text-secondaryMid dark:text-textWhite">
                  Programs
                </span>
              </div>
              <div className="flex flex-col items-center  w-full py-[3px]">
                <BellIcon className="w-3 h-3 relative" />
                <div className="absolute h-[4px] w-[4px] rounded-full bg-bgWhite mt-[2px] ml-[4px]">
                  <div className="h-[3px] w-[3px] rounded-full bg-primary"></div>
                </div>
                <span className="text-[4px] font-medium text-secondaryMid dark:text-textWhite">
                  Notifications
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
